// QuadrilateralLab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FONT } from "../typography";

/**
 * QuadrilateralLab
 * Modes:
 *  - "classify": fixed general quad by default, shape palette, no on-canvas labels, measures tables at bottom
 *  - "angles": shape palette, show interior angles + sum, show diagonal angles context
 *  - "rectangle": axis-aligned rectangle with edge handles to adjust length (w) & breadth (h) at 90°
 *  - "definer": simple canvas that highlights quadrilateral terminology (vertices, sides, diagonals, angles)
 *
 * Layout: toolbar (chips+palette) → SVG stage (fixed height) → measures bar (tables)
 */
export default function QuadrilateralLab({
  mode = "classify",
  allowVertexDrag = true,
  showGuides = true,
  showAngles,
  showInteriorAngles,
  showExteriorAngles = false,
  showAngleSum = mode === "angles",
  showDiagonalIntersection = mode !== "classify",
  snap = false,
  height = 420,
  startShape,
  length: rectLength = 12,   // alias to avoid naming collision with vlen()
  breadth: rectBreadth = 8,
  min = 1,
  max = 30,
  step = 1,
}) {
  const wrapRef = useRef(null);
  const W = 640;
  const H = 400;

  // Mode flags
  const _isClassify = mode === "classify";
  const _isAngles = mode === "angles";
  const _isRect = mode === "rectangle";
  const _isDefiner = mode === "definer";
  const _isDefinerAngles = mode === "definer-angles";
  const _isAngleLab = mode === "angle-lab";
  const _isTrapezium = mode === "trapezium";
  const _isParallelogram = mode === "parallelogram";

  const defaultAngleLabToggles = useMemo(
    () => ({
      showInterior: true,
      showExterior: false,
      showDiagonals: false,
      showLinearPairs: false,
      showSums: true,
      showWalkaround: false
    }),
    []
  );

  const [angleLabState, setAngleLabState] = useState(() => ({
    toggles: defaultAngleLabToggles,
    vertexSnapDeg: 0,
    autoMorph: 0,
    walkProgress: 0,
    targetPts: null,
    walkAccum: 0,
    walkDirection: 1
  }));

  const angleMorphTargetRef = useRef(null);
  const walkAnimationRef = useRef(null);
  const angleLabToggles = angleLabState.toggles;
  const angleLabWalkaroundActive = _isAngleLab && angleLabToggles.showWalkaround;

  const [trapeziumInfo, setTrapeziumInfo] = useState(null);
  const [parallelogramInfo, setParallelogramInfo] = useState(null);
  const parallelogramWalkRef = useRef(null);
  const [parallelogramWalk, setParallelogramWalk] = useState({ active: false, progress: 0 });

  useEffect(() => {
    if (_isAngleLab) {
      setAngleLabState((prev) => ({
        toggles: { ...defaultAngleLabToggles },
        vertexSnapDeg: 0,
        autoMorph: 0,
        walkProgress: 0,
        targetPts: null,
        walkAccum: 0,
        walkDirection: 1
      }));
    }
  }, [_isAngleLab, defaultAngleLabToggles]);

  useEffect(() => {
    if (!_isTrapezium) {
      setTrapeziumInfo(null);
    }
    if (!_isParallelogram) {
      setParallelogramInfo(null);
      setParallelogramWalk({ active: false, progress: 0 });
      if (parallelogramWalkRef.current) {
        cancelAnimationFrame(parallelogramWalkRef.current);
        parallelogramWalkRef.current = null;
      }
    }
  }, [_isTrapezium, _isParallelogram]);

  useEffect(() => {
    if (!_isAngleLab || angleLabState.autoMorph <= 0) {
      angleMorphTargetRef.current = null;
      return;
    }
    let frame;
    let last = 0;
    const tick = (timestamp) => {
      if (!last) last = timestamp;
      const delta = Math.min((timestamp - last) / 1000, 0.05);
      last = timestamp;
      setPts((prev) => {
        let target = angleMorphTargetRef.current;
        if (!target || target.length !== prev.length) {
          target = makeInitial("random", W, H);
          angleMorphTargetRef.current = target;
        }
        const speed = Math.min(Math.max(angleLabState.autoMorph, 0), 1);
        const factor = Math.min(delta * (0.5 + speed), 0.25);
        const next = prev.map((point, index) => {
          const dest = target[index] || target[target.length - 1];
          return {
            x: point.x + (dest.x - point.x) * factor,
            y: point.y + (dest.y - point.y) * factor
          };
        });
        const reached = next.every((point, index) => {
          const dest = target[index] || target[target.length - 1];
          return Math.abs(point.x - dest.x) < 0.5 && Math.abs(point.y - dest.y) < 0.5;
        });
        if (reached) {
          angleMorphTargetRef.current = makeInitial("random", W, H);
        }
        return next;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [_isAngleLab, angleLabState.autoMorph, W, H]);

  const _showInterior = _isAngles
    ? true
    : _isDefiner || _isDefinerAngles
    ? false
    : (showAngles ?? showInteriorAngles ?? false);
  const baseShowOnCanvasAngleNumbers = _isAngles;
  const _showVertexLabels =
    _isDefiner || _isDefinerAngles || _isAngleLab || _isTrapezium || (!_isClassify && !_isRect); // keep canvas de-cluttered in classify & rectangle

  // Initial shape
  const initialKind =
    startShape ||
    (_isRect
      ? "rectangle"
      : _isClassify
      ? "general"
      : _isDefiner || _isDefinerAngles || _isAngleLab
      ? "balanced"
      : _isTrapezium
      ? "trapezium"
      : _isParallelogram
      ? "parallelogram"
      : "rectangle");
  const [pts, setPts] = useState(() => makeInitial(initialKind, W, H));
  const [drag, setDrag] = useState(null);

  // Rectangle mode uses locked 90°; drive points from rect state
  const [rect, setRect] = useState(() => makeRect(rectLength, rectBreadth, W, H, min, max));
  const rectPts = useMemo(() => rectToPts(rect), [rect]);
  useEffect(() => { if (_isRect) setPts(rectPts); }, [rectPts, _isRect]);

  const [anglesStoryboard, setAnglesStoryboard] = useState("none");
  useEffect(() => {
    if (!_isDefinerAngles) {
      setAnglesStoryboard("none");
    }
  }, [_isDefinerAngles]);

  const _showOnCanvasAngleNumbers = baseShowOnCanvasAngleNumbers ||
    (_isDefinerAngles && ["interior", "sum", "exterior"].includes(anglesStoryboard)) ||
    (_isAngleLab && angleLabToggles.showInterior);

  // Ensure CW order
  const ordered = useMemo(() => enforceOrder(_isRect ? rectPts : pts), [_isRect, rectPts, pts]);

  // Vertex drag (disabled in rectangle mode)
  const enableVertexDrag = allowVertexDrag && !_isRect && !_isDefiner && !_isDefinerAngles && !_isClassify;

  useEffect(() => {
    if (!enableVertexDrag) return;
    const onMove = (e) => {
      if (drag == null) return;
      const box = wrapRef.current?.getBoundingClientRect(); if (!box) return;
      const px = ((e.clientX - box.left) / box.width) * W;
      const py = ((e.clientY - box.top)  / box.height) * H;
      const nx = clamp(snap ? Math.round(px / 10) * 10 : px, 10, W - 10);
      const ny = clamp(snap ? Math.round(py / 10) * 10 : py, 10, H - 10);
      setPts((prev) => {
        if (drag == null) return prev;
        const centre = centroid(prev);
        const nextPts = prev.map((point, index) => {
          if (index !== drag) return point;
          let candidate = { x: nx, y: ny };
          if (_isAngleLab && angleLabState.vertexSnapDeg > 0) {
            candidate = snapPointToAngle(candidate, centre, angleLabState.vertexSnapDeg);
          }
          return candidate;
        });
        return nextPts;
      });
    };
    const end = () => setDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [drag, enableVertexDrag, snap, _isAngleLab, angleLabState.vertexSnapDeg]);

  useEffect(() => {
    if (!_isAngleLab || !angleLabWalkaroundActive) {
      if (walkAnimationRef.current) {
        cancelAnimationFrame(walkAnimationRef.current);
        walkAnimationRef.current = null;
      }
      setAngleLabState((prev) =>
        prev.walkProgress === 0 && prev.walkAccum === 0
          ? prev
          : { ...prev, walkProgress: 0, walkAccum: 0 }
      );
      return;
    }
    let last = 0;
    const step = (timestamp) => {
      if (!last) last = timestamp;
      const delta = Math.min((timestamp - last) / 1000, 0.05);
      last = timestamp;
      setAngleLabState((prev) => {
        const speed = 0.1 + prev.autoMorph * 0.2;
        const nextProgress = (prev.walkProgress + delta * speed) % 1;
        const nextAccum = (prev.walkAccum + delta * speed * 360) % 360;
        if (nextProgress === prev.walkProgress && nextAccum === prev.walkAccum) return prev;
        return { ...prev, walkProgress: nextProgress, walkAccum: nextAccum };
      });
      walkAnimationRef.current = requestAnimationFrame(step);
    };
    walkAnimationRef.current = requestAnimationFrame(step);
    return () => {
      if (walkAnimationRef.current) cancelAnimationFrame(walkAnimationRef.current);
      walkAnimationRef.current = null;
    };
  }, [_isAngleLab, angleLabWalkaroundActive]);

  useEffect(() => {
    if (!_isParallelogram || !parallelogramWalk.active) {
      if (parallelogramWalkRef.current) {
        cancelAnimationFrame(parallelogramWalkRef.current);
        parallelogramWalkRef.current = null;
      }
      return;
    }
    let last = 0;
    const tick = (timestamp) => {
      if (!last) last = timestamp;
      const delta = Math.min((timestamp - last) / 1000, 0.05);
      last = timestamp;
      setParallelogramWalk((prev) => ({
        active: true,
        progress: (prev.progress + delta * 0.25) % 1
      }));
      parallelogramWalkRef.current = requestAnimationFrame(tick);
    };
    parallelogramWalkRef.current = requestAnimationFrame(tick);
    return () => {
      if (parallelogramWalkRef.current) cancelAnimationFrame(parallelogramWalkRef.current);
      parallelogramWalkRef.current = null;
    };
  }, [_isParallelogram, parallelogramWalk.active]);

  // Rectangle edge drag (right/bottom midpoints)
  const [edgeDrag, setEdgeDrag] = useState(null); // "w" | "h" | null
  useEffect(() => {
    if (!_isRect) return;
    const onMove = (e) => {
      if (!edgeDrag) return;
      const box = wrapRef.current?.getBoundingClientRect(); if (!box) return;
      const px = ((e.clientX - box.left) / box.width) * W;
      const py = ((e.clientY - box.top)  / box.height) * H;
      const cx = rect.cx, cy = rect.cy;
      if (edgeDrag === "w") {
        const halfW = clamp(Math.abs(px - cx), 10, (max * PIXELS_PER_UNIT));
        const newLen = clamp(halfW / PIXELS_PER_UNIT, min, max) * 2;
        setRect((r) => ({ ...r, wUnits: roundTo(newLen, step) }));
      } else if (edgeDrag === "h") {
        const halfH = clamp(Math.abs(py - cy), 10, (max * PIXELS_PER_UNIT));
        const newBr = clamp(halfH / PIXELS_PER_UNIT, min, max) * 2;
        setRect((r) => ({ ...r, hUnits: roundTo(newBr, step) }));
      }
    };
    const end = () => setEdgeDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [_isRect, edgeDrag, rect, min, max, step]);

  // Geometry
  const AB = vec(ordered[0], ordered[1]);
  const BC = vec(ordered[1], ordered[2]);
  const CD = vec(ordered[2], ordered[3]);
  const DA = vec(ordered[3], ordered[0]);

  const len = useMemo(() => ({
    AB: vlen(AB), BC: vlen(BC), CD: vlen(CD), DA: vlen(DA),
  }), [ordered]);

  const ang = useMemo(() => ({
    A: angleAt(ordered[3], ordered[0], ordered[1]),
    B: angleAt(ordered[0], ordered[1], ordered[2]),
    C: angleAt(ordered[1], ordered[2], ordered[3]),
    D: angleAt(ordered[2], ordered[3], ordered[0]),
  }), [ordered]);
  const angleSum = ang.A + ang.B + ang.C + ang.D;

  const diag = useMemo(() => ({
    AC: { p: ordered[0], q: ordered[2], v: vec(ordered[0], ordered[2]) },
    BD: { p: ordered[1], q: ordered[3], v: vec(ordered[1], ordered[3]) },
  }), [ordered]);

  const center = useMemo(() => {
    const total = ordered.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: total.x / ordered.length, y: total.y / ordered.length };
  }, [ordered]);

  const vertexLabels = useMemo(
    () => ordered.map((vertex) => computeVertexLabel(vertex, center)),
    [ordered, center]
  );

  const angleLabels = useMemo(
    () => ordered.map((vertex) => computeAngleLabel(vertex, center)),
    [ordered, center]
  );

  const sideLabels = useMemo(
    () => ({
      AB: computeSideLabel(ordered[0], ordered[1], center),
      BC: computeSideLabel(ordered[1], ordered[2], center),
      CD: computeSideLabel(ordered[2], ordered[3], center),
      DA: computeSideLabel(ordered[3], ordered[0], center)
    }),
    [ordered, center]
  );

  const diagonalLabels = useMemo(
    () => ({
      AC: computeDiagonalLabel(ordered[0], ordered[2]),
      BD: computeDiagonalLabel(ordered[1], ordered[3])
    }),
    [ordered]
  );

  const parallel = useMemo(() => ({
    AB_CD: isParallel(AB, CD),
    BC_DA: isParallel(BC, DA),
    anyPair: isParallel(AB, CD) || isParallel(BC, DA),
    bothPairs: isParallel(AB, CD) && isParallel(BC, DA),
  }), [ordered]);

  const diagonalsPerp = isPerpendicular(diag.AC.v, diag.BD.v);
  const X = lineIntersect(diag.AC.p, diag.AC.q, diag.BD.p, diag.BD.q);

  // Derived
  const polyArea = Math.abs(polygonArea(ordered));
  const polyPerimeter = len.AB + len.BC + len.CD + len.DA;
  const diagLen = { AC: vlen(diag.AC.v), BD: vlen(diag.BD.v) };

  const definerAnglesView = _isDefinerAngles ? anglesStoryboard : null;
  const showDefinerInterior = _isDefinerAngles && ["interior", "sum", "exterior"].includes(anglesStoryboard);
  const showDefinerSum = _isDefinerAngles && anglesStoryboard === "sum";
  const showDefinerExterior = _isDefinerAngles && anglesStoryboard === "exterior";
  const showDefinerDiagonalAngles = _isDefinerAngles && anglesStoryboard === "diagonals";

  const showAngleLabInterior = _isAngleLab && angleLabToggles.showInterior;
  const showAngleLabExterior = _isAngleLab && angleLabToggles.showExterior;
  const showAngleLabDiagonals = _isAngleLab && angleLabToggles.showDiagonals;
  const showAngleLabLinearPairs = _isAngleLab && angleLabToggles.showLinearPairs;
  const showAngleLabSums = _isAngleLab && angleLabToggles.showSums;
  const showAngleLabWalkaround = angleLabWalkaroundActive;

  let showDiagonalAC = true;
  let showDiagonalBD = true;
  let shouldShowIntersection = showDiagonalIntersection;

  if (_isDefinerAngles) {
    showDiagonalAC = anglesStoryboard === "sum" || anglesStoryboard === "diagonals";
    showDiagonalBD = anglesStoryboard === "diagonals";
    shouldShowIntersection = anglesStoryboard === "diagonals";
  } else if (_isAngleLab) {
    showDiagonalAC = showAngleLabDiagonals;
    showDiagonalBD = showAngleLabDiagonals;
    shouldShowIntersection = showAngleLabDiagonals;
  }

  const renderInteriorArcs = _showInterior || showDefinerInterior || showAngleLabInterior;
  const renderExteriorGuides = showDefinerExterior || showAngleLabExterior;
  const showAngleLabelsOnCanvas = _showOnCanvasAngleNumbers || (_isDefinerAngles && showDefinerInterior) || (_isAngleLab && showAngleLabInterior);

  const definerAnglesSteps = [
    {
      id: "interior",
      label: "Interior Angles",
      blurb: "Reveal the four corner angles and label each as ∠A, ∠B, ∠C, ∠D."
    },
    {
      id: "sum",
      label: "Sum of Interior Angles",
      blurb: "Draw diagonal AC to split the quadrilateral into two triangles and note that their angles add up to 360°."
    },
    {
      id: "exterior",
      label: "Exterior Angles",
      blurb: "Extend a side at every vertex to form linear pairs and note each exterior angle = 180° − interior angle."
    },
    {
      id: "diagonals",
      label: "Diagonal Angles",
      blurb: "Add both diagonals and examine the four angles created at their intersection."
    }
  ];

  const definerAnglesDescription =
    definerAnglesSteps.find((step) => step.id === anglesStoryboard)?.blurb ||
    "Start with the blank quadrilateral. Pick a focus to explore interior, exterior, and diagonal angle relationships.";

  // Shape palette
  const palette = [
    { key: "rectangle",     label: "Rectangle" },
    { key: "square",        label: "Square" },
    { key: "parallelogram", label: "Parallelogram" },
    { key: "rhombus",       label: "Rhombus" },
    { key: "kite",          label: "Kite" },
    { key: "trapezium",     label: "Trapezium" },
  ];
  function setShape(kind) {
    if (_isRect) {
      if (kind === "rectangle") {
        setRect(makeRect(rectLength, rectBreadth, W, H, min, max));
      } else if (kind === "square") {
        const s = Math.max(Math.min(rectLength, rectBreadth), min);
        setRect(makeRect(s, s, W, H, min, max));
      } else {
        setPts(makeInitial(kind, W, H));
      }
    } else {
      setPts(makeInitial(kind, W, H));
    }
  }
  function resetGeneral() { setPts(makeInitial("general", W, H)); }

  // Classification string
  const classification = useMemo(() => {
    const rightAnglesCount = [ang.A, ang.B, ang.C, ang.D].filter(a => approx(a, 90, 1.5)).length;
    const allSidesEqual = approx(len.AB, len.BC) && approx(len.BC, len.CD) && approx(len.CD, len.DA);
    const adjPairsEqual =
      (approx(len.AB, len.BC) && approx(len.CD, len.DA)) ||
      (approx(len.BC, len.CD) && approx(len.DA, len.AB));
    if (allSidesEqual && rightAnglesCount === 4) return "Square";
    if (parallel.bothPairs && rightAnglesCount === 4) return "Rectangle";
    if (allSidesEqual && rightAnglesCount !== 4)   return "Rhombus";
    if (parallel.bothPairs)                        return "Parallelogram";
    if (adjPairsEqual)                             return "Kite";
    if (parallel.anyPair)                          return "Trapezium";
    return "General Quadrilateral";
  }, [ang, len, parallel]);

  const interiorAngles = [ang.A, ang.B, ang.C, ang.D];
  const exteriorAngles = interiorAngles.map((value) => 180 - value);
  const exteriorSum = exteriorAngles.reduce((acc, value) => acc + value, 0);
  const linearPairChecks = interiorAngles.map((value, index) => approx(value + exteriorAngles[index], 180, 0.75));
  const perimeterMeta = useMemo(() => buildPerimeterMeta(ordered), [ordered]);
  const walkState = showAngleLabWalkaround ? pointAlongPerimeter(ordered, perimeterMeta, angleLabState.walkProgress) : null;
  const walkFacing = walkState ? walkState.heading : 0;
  const trapeziumPerimeter = len.AB + len.BC + len.CD + len.DA;
  const parallelogramWalkState = _isParallelogram && parallelogramWalk.active
    ? pointAlongPerimeter(ordered, perimeterMeta, parallelogramWalk.progress)
    : null;

  const resetParallelogramWalk = () => {
    if (parallelogramWalkRef.current) {
      cancelAnimationFrame(parallelogramWalkRef.current);
      parallelogramWalkRef.current = null;
    }
    setParallelogramWalk({ active: false, progress: 0 });
  };

  const handleTrapeziumPreset = (variant) => {
    setTrapeziumInfo(null);
    setPts(makeTrapeziumPreset(variant, W, H));
  };

  const handleParallelogramInfo = (type) => {
    setParallelogramInfo({ type });
    if (type === "perimeter") {
      setParallelogramWalk({ active: true, progress: 0 });
    } else {
      resetParallelogramWalk();
    }
  };

  return (
    <div
      ref={wrapRef}
      className="qlab"
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-soft)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-soft)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%"
      }}
    >
      {/* Toolbar (no absolute positioning) */}
      <div
        className="qlab-toolbar"
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "space-between",
          alignItems: "center",
          padding: 8,
          background: "var(--surface-subtle)",
          borderBottom: "1px solid var(--border-soft)",
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {_isDefiner ? (
            <>
              <Chip label="Mode: Definer" tone="info" />
              <span style={{ color: "var(--ink-muted)", fontSize: FONT.sm }}>
                Familiarise yourself with vertices, sides, angles, and diagonals.
              </span>
            </>
          ) : _isDefinerAngles ? (
            <>
              <Chip label="Mode: Angle Explorer" tone="info" />
              <span style={{ color: "var(--ink-muted)", fontSize: FONT.sm }}>
                Start blank, then reveal angle relationships step by step.
              </span>
              {anglesStoryboard !== "none" && (
                <Chip
                  label={{
                    interior: "Showing interior angles",
                    sum: "Triangles show 360°",
                    exterior: "Linear pairs",
                    diagonals: "Diagonal intersections"
                  }[anglesStoryboard] || ""
                }
                  tone="info"
                />
              )}
            </>
          ) : _isTrapezium ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => handleTrapeziumPreset("isosceles")}>Isosceles Trapezium</button>
                <button type="button" className="btn btn-secondary" onClick={() => handleTrapeziumPreset("scalene")}>Scalene Trapezium</button>
                <button type="button" className="btn btn-secondary" onClick={() => handleTrapeziumPreset("right")}>Right Angled Trapezium</button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setTrapeziumInfo({ type: "perimeter" })}>Perimeter</button>
                <button type="button" className="btn btn-secondary" onClick={() => setTrapeziumInfo({ type: "area" })}>Area</button>
              </div>
            </div>
          ) : _isParallelogram ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setPts(makeInitial("parallelogram", W, H));
                    setParallelogramInfo(null);
                    resetParallelogramWalk();
                  }}
                >
                  Reset Parallelogram
                </button>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => handleParallelogramInfo("perimeter")}>Perimeter</button>
                <button type="button" className="btn btn-secondary" onClick={() => handleParallelogramInfo("area")}>Area</button>
              </div>
            </div>
          ) : (
            <>
              {!_isTrapezium && !_isParallelogram && <Chip label={`Type: ${classification}`} tone="info" />}
              {_isAngles && (
                <Chip label={`∠ sum: ${Math.round(angleSum)}°`} tone={approx(angleSum, 360, 1) ? "ok" : "warn"} />
              )}
              {!_isTrapezium && !_isParallelogram && <Chip label={`Mode: ${_isRect ? "Rectangle" : _isAngles ? "Angles" : "Classify"}`} />}
              {_isClassify && (
                <button className="btn btn-secondary" onClick={resetGeneral} title="Reset to general quadrilateral">
                  Reset
                </button>
              )}
            </>
          )}
        </div>

        {!_isDefiner && !_isDefinerAngles && !_isAngleLab && !_isTrapezium && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {palette.map((p) => (
              <button key={p.key} className="btn btn-secondary" onClick={() => setShape(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {(() => {
        const stage = (
          <div
            className="qlab-stage"
            style={{
              position: "relative",
              width: "100%",
              height,
              background: "transparent"
            }}
          >
          <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
          {/* Guides */}
          {showGuides && (
            <g opacity="0.15">
              {Array.from({ length: Math.floor(W / 40) - 1 }).map((_, i) => {
                const x = (i + 1) * 40;
                return <line key={`vx-${x}`} x1={x} y1={0} x2={x} y2={H} stroke="var(--ink)" strokeWidth="1" />;
              })}
              {Array.from({ length: Math.floor(H / 40) - 1 }).map((_, i) => {
                const y = (i + 1) * 40;
                return <line key={`hz-${y}`} x1={0} y1={y} x2={W} y2={y} stroke="var(--ink)" strokeWidth="1" />;
              })}
            </g>
          )}

          {/* Diagonals */}
          {(showDiagonalAC || showDiagonalBD) && (
            <g opacity="0.35">
              {showDiagonalAC && (
                <line
                  x1={ordered[0].x}
                  y1={ordered[0].y}
                  x2={ordered[2].x}
                  y2={ordered[2].y}
                  stroke="var(--accent-info)"
                  strokeDasharray="6 6"
                />
              )}
              {showDiagonalBD && (
                <line
                  x1={ordered[1].x}
                  y1={ordered[1].y}
                  x2={ordered[3].x}
                  y2={ordered[3].y}
                  stroke="var(--accent-info)"
                  strokeDasharray="6 6"
                />
              )}
            </g>
          )}

          {/* Polygon */}
          <polygon
            points={ordered.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="rgba(106,169,255,0.20)"
            stroke="var(--accent-info)"
            strokeWidth="2"
          />

          {/* Angle sum triangles */}
          {showDefinerSum && (
            <g opacity="0.45">
              <polygon
                points={[ordered[0], ordered[1], ordered[2]].map((p) => `${p.x},${p.y}`).join(" ")}
                fill="rgba(251, 191, 36, 0.35)"
                stroke="rgba(251, 191, 36, 0.85)"
                strokeWidth="2"
              />
              <polygon
                points={[ordered[0], ordered[2], ordered[3]].map((p) => `${p.x},${p.y}`).join(" ")}
                fill="rgba(59, 130, 246, 0.28)"
                stroke="rgba(59, 130, 246, 0.75)"
                strokeWidth="2"
              />
              <text
                x={midpoint(midpoint(ordered[0], ordered[1]), midpoint(ordered[0], ordered[2])).x}
                y={midpoint(midpoint(ordered[0], ordered[1]), midpoint(ordered[0], ordered[2])).y}
                fontSize={FONT.sm}
                fontWeight={600}
                fill="rgba(180, 83, 9, 0.95)"
                textAnchor="middle"
              >
                ΔABC
              </text>
              <text
                x={midpoint(midpoint(ordered[0], ordered[2]), midpoint(ordered[0], ordered[3])).x}
                y={midpoint(midpoint(ordered[0], ordered[2]), midpoint(ordered[0], ordered[3])).y}
                fontSize={FONT.sm}
                fontWeight={600}
                fill="rgba(37, 99, 235, 0.95)"
                textAnchor="middle"
              >
                ΔACD
              </text>
            </g>
          )}

          {/* Exterior rays */}
          {renderExteriorGuides &&
            ordered.map((p, i) => {
              const prev = ordered[(i + 3) % 4];
              const next = ordered[(i + 1) % 4];
              const extPrev = extendBeyondVertex(prev, p, 90);
              const extNext = extendBeyondVertex(next, p, 90);
              return (
                <g key={`ext-${i}`} opacity="0.6">
                  <line
                    x1={p.x}
                    y1={p.y}
                    x2={extPrev.x}
                    y2={extPrev.y}
                    stroke={_isAngleLab ? "rgba(251, 146, 60, 0.85)" : "rgba(248, 113, 113, 0.9)"}
                    strokeWidth="2"
                    strokeDasharray="4 4"
                  />
                  <line
                    x1={p.x}
                    y1={p.y}
                    x2={extNext.x}
                    y2={extNext.y}
                    stroke={_isAngleLab ? "rgba(251, 146, 60, 0.85)" : "rgba(248, 113, 113, 0.9)"}
                    strokeWidth="2"
                    strokeDasharray="4 4"
                  />
                </g>
              );
            })}

          {/* Exterior angle arcs */}
          {renderExteriorGuides &&
            ordered.map((p, i) => {
              const prev = ordered[(i + 3) % 4];
              const next = ordered[(i + 1) % 4];
              const extPrev = extendBeyondVertex(prev, p, 70);
              const arc = arcPath(p, extPrev, next, 26);
              const label = externalAngleLabelPosition(p, extPrev, next, 40);
              return (
                <g key={`ext-arc-${i}`}>
                  <path
                    d={arc}
                    fill={_isAngleLab ? "rgba(251, 146, 60, 0.18)" : "rgba(248, 113, 113, 0.18)"}
                    stroke={_isAngleLab ? "rgba(251, 146, 60, 0.65)" : "rgba(248, 113, 113, 0.65)"}
                  />
                  <text
                    x={label.x}
                    y={label.y}
                    fontSize={FONT.sm}
                    fill={_isAngleLab ? "rgba(194, 65, 12, 0.95)" : "rgba(248, 113, 113, 0.95)"}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontWeight={600}
                  >
                    {`180° − ∠${labelFor(i)}`}
                  </text>
                </g>
              );
            })}

          {showAngleLabLinearPairs &&
            ordered.map((p, i) => {
              const labelPoint = offsetPoint(p, { x: p.x - center.x, y: p.y - center.y }, 60);
              const sum = Math.round(interiorAngles[i] + exteriorAngles[i]);
              const ok = linearPairChecks[i];
              return (
                <g key={`lp-${i}`}>
                  <text
                    x={labelPoint.x}
                    y={labelPoint.y}
                    fontSize={FONT.sm}
                    fill={ok ? "rgba(34,197,94,0.9)" : "rgba(248,113,113,0.9)"}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontWeight={600}
                  >
                    ∠{labelFor(i)} + ext = {sum}°
                  </text>
                  {ok && (
                    <text
                      x={labelPoint.x}
                      y={labelPoint.y + 12}
                      fontSize={FONT.sm}
                      fill="rgba(34,197,94,0.85)"
                      textAnchor="middle"
                    >
                      linear pair ✓
                    </text>
                  )}
                </g>
              );
            })}

          {/* Diagonal angle arcs */}
          {showDefinerDiagonalAngles && X && (
            <g opacity="0.9">
              {buildDiagonalAngleArcs(X, ordered).map((arc, idx) => (
                <path key={`diag-arc-${idx}`} d={arc} fill="none" stroke="rgba(59, 130, 246, 0.85)" strokeWidth="3" />
              ))}
            </g>
          )}

          {/* Interiors (Angles mode only) */}
          {renderInteriorArcs &&
            ordered.map((p, i) => {
              const prev = ordered[(i + 3) % 4];
              const next = ordered[(i + 1) % 4];
              const Adeg = angleAt(prev, p, next);
              const arcRadius = _isDefinerAngles ? 24 : _isAngleLab ? 26 : 18;
              const arc = arcPath(p, prev, next, arcRadius);
              const label = angleLabels[i];
              return (
                <g key={`ang-${i}`}>
                  <path
                    d={arc}
                    fill={
                      _isAngleLab
                        ? "rgba(59,130,246,0.22)"
                        : _isDefinerAngles
                        ? "rgba(56,189,248,0.28)"
                        : "rgba(255,255,255,0.08)"
                    }
                    stroke={
                      _isAngleLab
                        ? "rgba(59,130,246,0.85)"
                        : _isDefinerAngles
                        ? "rgba(56,189,248,0.75)"
                        : "rgba(255,255,255,0.35)"
                    }
                  />
                  {showAngleLabelsOnCanvas && (
                    <text
                      x={label.x}
                      y={label.y}
                      fontSize={FONT.sm}
                      fill={_isAngleLab || _isDefinerAngles ? "var(--ink-strong)" : "var(--ink)"}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontWeight={_isAngleLab || _isDefinerAngles ? 700 : 500}
                    >
                      {_isAngleLab || _isDefinerAngles ? `∠${labelFor(i)} = ${Math.round(Adeg)}°` : Math.round(Adeg) + "°"}
                    </text>
                  )}
                </g>
              );
            })}

          {/* Diagonal intersection */}
          {shouldShowIntersection && X && <circle cx={X.x} cy={X.y} r="4.5" fill="rgba(34,197,94,0.9)" />}

          {/* Vertex handles (not in rectangle mode) */}
          {!_isRect &&
            ordered.map((p, i) => {
              const vLabel = vertexLabels[i];
              return (
                <g
                  key={`pt-${i}`}
                  onPointerDown={() => enableVertexDrag && setDrag(i)}
                  style={{ cursor: enableVertexDrag ? "grab" : "default" }}
                >
                  <circle cx={p.x} cy={p.y} r="7.5" fill="var(--btn-primary-bg)" stroke="var(--btn-primary-fg)" strokeWidth="1.5" />
                  {_showVertexLabels && (
                    <text
                      x={vLabel.x}
                      y={vLabel.y}
                      fontSize={FONT.sm}
                      fill="var(--ink)"
                      textAnchor={vLabel.anchor}
                      dominantBaseline="middle"
                    >
                      {labelFor(i)}
                    </text>
                  )}
                </g>
              );
            })}

          {/* Rectangle edge handles */}
          {_isRect && (
            <>
              <g onPointerDown={() => setEdgeDrag("w")} style={{ cursor: "ew-resize" }}>
                <rect x={rect.cx + rect.w / 2 - 6} y={rect.cy - 6} width={12} height={12}
                      fill="var(--btn-primary-bg)" stroke="var(--btn-primary-fg)" strokeWidth="1.5" rx="2" ry="2" />
              </g>
              <g onPointerDown={() => setEdgeDrag("h")} style={{ cursor: "ns-resize" }}>
                <rect x={rect.cx - 6} y={rect.cy + rect.h / 2 - 6} width={12} height={12}
                      fill="var(--btn-primary-bg)" stroke="var(--btn-primary-fg)" strokeWidth="1.5" rx="2" ry="2" />
              </g>
            </>
          )}

          {_isDefiner && (
            <g className="qlab-definer-labels" fontSize={FONT.base} fill="var(--ink)" fontWeight={700}>
              {Object.entries(sideLabels).map(([key, info]) => (
                <text
                  key={`side-${key}`}
                  x={info.x}
                  y={info.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${info.rotation} ${info.x} ${info.y})`}
                >
                  {key}
                </text>
              ))}
              {Object.entries(diagonalLabels).map(([key, info]) => (
                <text
                  key={`diag-${key}`}
                  x={info.x}
                  y={info.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${info.rotation} ${info.x} ${info.y})`}
                >
                  {key}
                </text>
              ))}
              {angleLabels.map((info, index) => (
                <text
                  key={`angle-${index}`}
                  x={info.x}
                  y={info.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={FONT.sm}
                  fontWeight={600}
                >
                  {`∠${labelFor(index)}`}
                </text>
              ))}
            </g>
          )}

          {showAngleLabWalkaround && walkState && (
            <g transform={`translate(${walkState.point.x} ${walkState.point.y})`}>
              <circle r="8" fill="rgba(251,191,36,0.85)" stroke="rgba(194,65,12,0.9)" strokeWidth="2" />
              <line
                x1="0"
                y1="0"
                x2={Math.cos(walkFacing) * 18}
                y2={Math.sin(walkFacing) * 18}
                stroke="rgba(194,65,12,0.9)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </g>
          )}

          {_isParallelogram && parallelogramWalkState && (
            <g transform={`translate(${parallelogramWalkState.point.x} ${parallelogramWalkState.point.y})`}>
              <circle r="8" fill="rgba(56,189,248,0.9)" stroke="rgba(14,165,233,0.9)" strokeWidth="2" />
              <line
                x1="0"
                y1="0"
                x2={Math.cos(parallelogramWalkState.heading) * 18}
                y2={Math.sin(parallelogramWalkState.heading) * 18}
                stroke="rgba(14,165,233,0.9)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </g>
          )}
          </svg>

          {showAngleLabSums && (
            <div
              style={{
                position: "absolute",
                left: 16,
                bottom: 16,
                padding: "10px 14px",
                borderRadius: 12,
                background: "rgba(15,23,42,0.8)",
                color: "var(--ink)",
                display: "flex",
                gap: 16,
                fontWeight: 700,
                fontSize: FONT.base,
                border: "1px solid rgba(148,163,184,0.35)"
              }}
            >
              <span>Σ interior = {Math.round(angleSum)}°</span>
              <span>Σ exterior = {Math.round(exteriorSum)}°</span>
            </div>
          )}

          {showAngleLabWalkaround && (
            <div
              style={{
                position: "absolute",
                right: 16,
                bottom: 16,
                padding: "10px 14px",
                borderRadius: 12,
                background: "rgba(15,23,42,0.75)",
                color: "var(--ink)",
                fontSize: FONT.sm,
                border: "1px solid rgba(254,215,170,0.35)"
              }}
            >
              Turned so far: {Math.round(angleLabState.walkAccum)}°
            </div>
          )}

          {_isTrapezium && trapeziumInfo && (
            (() => {
              const areaDetails = trapeziumInfo.type === "area" ? computeTrapeziumAreaDetails(ordered) : null;
              return (
                <div
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    maxWidth: 320,
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "rgba(12,18,28,0.92)",
                    color: "var(--ink)",
                    border: "1px solid rgba(148,163,184,0.35)",
                    boxShadow: "var(--shadow-soft)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ fontSize: FONT.base }}>
                      {trapeziumInfo.type === "perimeter" ? "Perimeter" : "Area Derivation"}
                    </strong>
                    <button
                      type="button"
                      className="btn btn-tertiary"
                      onClick={() => setTrapeziumInfo(null)}
                      style={{ padding: "2px 6px" }}
                    >
                      ✕
                    </button>
                  </div>
                  {trapeziumInfo.type === "perimeter" ? (
                    <div style={{ fontSize: FONT.sm, lineHeight: 1.4 }}>
                      <p style={{ margin: 0 }}>Sum of the four sides:</p>
                      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                        <li>AB = {fmt(len.AB)}</li>
                        <li>BC = {fmt(len.BC)}</li>
                        <li>CD = {fmt(len.CD)}</li>
                        <li>DA = {fmt(len.DA)}</li>
                      </ul>
                      <p style={{ margin: "6px 0 0" }}><strong>P = {fmt(trapeziumPerimeter)}</strong></p>
                    </div>
                  ) : areaDetails ? (
                    <div style={{ fontSize: FONT.sm, lineHeight: 1.45, display: "flex", flexDirection: "column", gap: 6 }}>
                      <p style={{ margin: 0 }}>
                        Drop perpendiculars from the shorter base to the longer base (length a = {fmt(areaDetails.longerLength)}).
                        Both perpendiculars share height h = {fmt(areaDetails.height)}.
                      </p>
                      <ol style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Rectangle width b = {fmt(areaDetails.rectangleWidth)} ⇒ area b × h = {fmt(areaDetails.rectangleArea)}</li>
                        <li>
                          Left triangle: base a₁ = {fmt(areaDetails.a1)} ⇒ ½ × a₁ × h = {fmt(areaDetails.triangleLeft)}
                        </li>
                        <li>
                          Right triangle: base a₂ = {fmt(areaDetails.a2)} ⇒ ½ × a₂ × h = {fmt(areaDetails.triangleRight)}
                        </li>
                      </ol>
                      <p style={{ margin: 0 }}>
                        Combine: <strong>A = ½ × (a + b) × h = {fmt(areaDetails.total)}</strong>
                      </p>
                    </div>
                  ) : (
                    <div style={{ fontSize: FONT.sm }}>Unable to derive area for this shape – ensure one pair of sides stays parallel.</div>
                  )}
                </div>
              );
            })()
          )}

          {_isParallelogram && parallelogramInfo && (
            (() => {
              const areaDetails = parallelogramInfo.type === "area" ? computeParallelogramAreaDetails(ordered) : null;
              return (
                <div
                  style={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    maxWidth: 320,
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "rgba(12,18,28,0.92)",
                    color: "var(--ink)",
                    border: "1px solid rgba(148,163,184,0.35)",
                    boxShadow: "var(--shadow-soft)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ fontSize: FONT.base }}>
                      {parallelogramInfo.type === "perimeter" ? "Perimeter Walk" : "Area Derivation"}
                    </strong>
                    <button
                      type="button"
                      className="btn btn-tertiary"
                      onClick={() => {
                        setParallelogramInfo(null);
                        resetParallelogramWalk();
                      }}
                      style={{ padding: "2px 6px" }}
                    >
                      ✕
                    </button>
                  </div>
                  {parallelogramInfo.type === "perimeter" ? (
                    <div style={{ fontSize: FONT.sm, lineHeight: 1.45, display: "flex", flexDirection: "column", gap: 6 }}>
                      <p style={{ margin: 0 }}>Walk the boundary to see opposing sides repeat:</p>
                      <ol style={{ margin: 0, paddingLeft: 18 }}>
                        <li>AB = {fmt(len.AB)} (call this a)</li>
                        <li>BC = {fmt(len.BC)} (call this b)</li>
                        <li>CD = {fmt(len.CD)} = a</li>
                        <li>DA = {fmt(len.DA)} = b</li>
                      </ol>
                      <p style={{ margin: 0 }}>
                        <strong>P = 2 × (a + b) = {fmt(2 * (len.AB + len.BC))}</strong>
                      </p>
                    </div>
                  ) : areaDetails ? (
                    <div style={{ fontSize: FONT.sm, lineHeight: 1.45, display: "flex", flexDirection: "column", gap: 6 }}>
                      <p style={{ margin: 0 }}>
                        Choose base AB with length a = {fmt(areaDetails.base)}. Drop a perpendicular from C to AB giving height h = {fmt(areaDetails.height)}.
                      </p>
                      <ol style={{ margin: 0, paddingLeft: 18 }}>
                        <li>
                          Diagonal AC splits the parallelogram into ΔABC and ΔCDA.
                        </li>
                        <li>
                          Area of ΔABC = ½ × a × h = {fmt(areaDetails.triangle)}.
                        </li>
                        <li>
                          ΔCDA is congruent ⇒ same area.
                        </li>
                      </ol>
                      <p style={{ margin: 0 }}>
                        <strong>Parallelogram area = 2 × {fmt(areaDetails.triangle)} = {fmt(areaDetails.total)} = a × h.</strong>
                      </p>
                    </div>
                  ) : (
                    <div style={{ fontSize: FONT.sm }}>Unable to compute area — ensure opposite sides remain parallel.</div>
                  )}
                </div>
              );
            })()
          )}
        </div>
        );

       if (_isAngleLab) {
         const toggleButton = (key, label) => {
           const active = angleLabToggles[key];
           return (
             <button
               key={key}
               type="button"
               className={`btn ${active ? "btn-primary" : "btn-secondary"}`}
               aria-pressed={active}
               onClick={() => setAngleLabState((prev) => ({
                 ...prev,
                 toggles: { ...prev.toggles, [key]: !prev.toggles[key] }
               }))}
             >
               {label}
             </button>
           );
         };

         const presetButton = (kind, label) => (
           <button
             key={kind}
             type="button"
             className="btn btn-secondary"
             onClick={() => {
               angleMorphTargetRef.current = null;
               setPts(makeInitial(kind, W, H));
               setAngleLabState((prev) => ({ ...prev, walkProgress: 0, walkAccum: 0 }));
             }}
           >
             {label}
           </button>
         );

         const actionButtons = [
           { key: "snap", label: "Snap Right Angles", action: () => setPts(makeInitial("rectangle", W, H)) },
           { key: "parallel", label: "Opposite Sides ∥", action: () => setPts(makeInitial("parallelogram", W, H)) },
           { key: "equalSides", label: "Equalize All Sides", action: () => setPts(makeInitial("rhombus", W, H)) },
           { key: "onePair", label: "One Pair ∥", action: () => setPts(makeInitial("trapezium", W, H)) }
         ];

         const challengeItems = [
           { key: "pair", label: "Linear Pair Check", done: angleLabToggles.showInterior && angleLabToggles.showExterior && showAngleLabLinearPairs && linearPairChecks.every(Boolean) },
           { key: "int360", label: "360° Interior", done: approx(angleSum, 360, 0.5) },
           { key: "ext360", label: "360° Exterior", done: approx(exteriorSum, 360, 0.8) && showAngleLabWalkaround },
           { key: "type", label: "Type Hunt", done: showAngleLabDiagonals && ["Rectangle", "Square", "Rhombus", "Parallelogram", "Kite"].includes(classification) },
           { key: "balance", label: "Angle Balancer", done: approx(interiorAngles[0] + interiorAngles[1], 180, 1.5) && approx(interiorAngles[2] + interiorAngles[3], 180, 1.5) }
         ];

         return (
           <div
             style={{
               display: "flex",
               gap: 16,
               padding: 12,
               alignItems: "stretch"
             }}
           >
             <aside
               style={{
                 flex: "0 0 260px",
                 display: "flex",
                 flexDirection: "column",
                 gap: 16,
                 padding: 16,
                 background: "var(--surface-subtle)",
                 border: "1px solid var(--border-soft)",
                 borderRadius: "var(--radius-md)",
                 boxShadow: "var(--shadow-soft)"
               }}
             >
               <div>
                 <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>Layers</h3>
                 <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                   {toggleButton("showInterior", "Interior Arcs")}
                   {toggleButton("showExterior", "Exterior Rays")}
                   {toggleButton("showDiagonals", "Show Diagonals")}
                   {toggleButton("showLinearPairs", "Linear Pair Helper")}
                   {toggleButton("showSums", "Show Sums")}
                   {toggleButton("showWalkaround", "Walkaround Pointer")}
                 </div>
               </div>

               <div>
                 <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>Presets</h3>
                 <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                   {presetButton("random", "Random")}
                   {presetButton("rectangle", "Rectangle")}
                   {presetButton("square", "Square")}
                   {presetButton("parallelogram", "Parallelogram")}
                   {presetButton("rhombus", "Rhombus")}
                   {presetButton("kite", "Kite")}
                   {presetButton("trapezium", "Trapezium")}
                 </div>
               </div>

               <div>
                 <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>Actions</h3>
                 <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                   {actionButtons.map((item) => (
                     <button
                       key={item.key}
                       type="button"
                       className="btn btn-secondary"
                       onClick={() => {
                         angleMorphTargetRef.current = null;
                         item.action();
                         setAngleLabState((prev) => ({ ...prev, walkProgress: 0, walkAccum: 0 }));
                       }}
                     >
                       {item.label}
                     </button>
                   ))}
                 </div>
               </div>

               <div>
                 <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>Settings</h3>
                 <label style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8, fontSize: FONT.sm, color: "var(--ink)" }}>
                   Vertex Snap (°)
                   <select
                     value={angleLabState.vertexSnapDeg}
                     onChange={(event) => setAngleLabState((prev) => ({
                       ...prev,
                       vertexSnapDeg: Number(event.target.value)
                     }))}
                     style={{ padding: "4px 6px", borderRadius: 6, border: "1px solid var(--border-soft)" }}
                   >
                     <option value={0}>Off</option>
                     <option value={15}>15°</option>
                     <option value={30}>30°</option>
                   </select>
                 </label>
                 <label style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12, fontSize: FONT.sm, color: "var(--ink)" }}>
                   Auto morph
                   <input
                     type="range"
                     min="0"
                     max="1"
                     step="0.05"
                     value={angleLabState.autoMorph}
                     onChange={(event) => setAngleLabState((prev) => ({
                       ...prev,
                       autoMorph: Number(event.target.value)
                     }))}
                   />
                   <span style={{ fontSize: FONT.sm, color: "var(--ink-muted)" }}>{(angleLabState.autoMorph * 100).toFixed(0)}% speed</span>
                 </label>
               </div>
             </aside>

             <div style={{ flex: 1, minWidth: 0 }}>{stage}</div>

             <aside
               style={{
                 flex: "0 0 260px",
                 display: "flex",
                 flexDirection: "column",
                 gap: 12,
                 padding: 16,
                 background: "var(--surface-subtle)",
                 border: "1px solid var(--border-soft)",
                 borderRadius: "var(--radius-md)",
                 boxShadow: "var(--shadow-soft)"
               }}
             >
               <div>
                 <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>Insights</h3>
                 <p style={{ margin: "6px 0 0", fontSize: FONT.base, lineHeight: 1.45, color: "var(--ink-muted)" }}>
                   Drag the vertices to experiment. Exterior turns, interior sums, and diagonal tricks reveal how every quadrilateral
                   balances to 360°.
                 </p>
               </div>
               <div>
                 <h4 style={{ margin: "8px 0", fontSize: FONT.base, color: "var(--ink)" }}>Challenges</h4>
                 <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                   {challengeItems.map((item) => (
                     <li key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: FONT.sm, color: "var(--ink)" }}>
                       <span style={{ color: item.done ? "rgba(34,197,94,0.9)" : "rgba(148,163,184,0.9)" }}>
                         {item.done ? "✔" : "○"}
                       </span>
                       {item.label}
                     </li>
                   ))}
                 </ul>
               </div>
               <div>
                 <h4 style={{ margin: "8px 0", fontSize: FONT.base, color: "var(--ink)" }}>Quick facts</h4>
                 <ul style={{ margin: 0, paddingLeft: 18, fontSize: FONT.sm, lineHeight: 1.4, color: "var(--ink-muted)" }}>
                   <li>Interior angles of any quadrilateral sum to 360°.</li>
                   <li>Exterior turn sum stays 360° no matter the shape.</li>
                   <li>Parallelogram ⇒ adjacent angles are supplementary.</li>
                   <li>Rhombus &amp; kite ⇒ diagonals meet at right angles.</li>
                 </ul>
               </div>
             </aside>
           </div>
         );
       }

       if (_isDefinerAngles) {
         return (
           <div
             style={{
               display: "flex",
               gap: 16,
               padding: 12,
               alignItems: "stretch"
             }}
           >
             <aside
               style={{
                 flex: "0 0 260px",
                 padding: "16px",
                 background: "var(--surface-subtle)",
                 border: "1px solid var(--border-soft)",
                 borderRadius: "var(--radius-md)",
                 boxShadow: "var(--shadow-soft)",
                 display: "flex",
                 flexDirection: "column",
                 gap: 12
               }}
             >
               <div>
                 <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>
                   Angle Storyboard
                 </h3>
                 <p style={{ margin: "4px 0 0", fontSize: FONT.base, lineHeight: 1.45, color: "var(--ink-muted)" }}>
                   Click a card to highlight a specific angle relationship. Reset returns to the blank shape.
                 </p>
               </div>
               <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                 {definerAnglesSteps.map((step) => {
                   const isActive = anglesStoryboard === step.id;
                   return (
                     <button
                       key={step.id}
                       type="button"
                       className={`btn ${isActive ? "btn-primary" : "btn-secondary"}`}
                       onClick={() => setAnglesStoryboard(step.id)}
                     >
                       {step.label}
                     </button>
                   );
                 })}
                 <button
                   type="button"
                   className="btn btn-secondary"
                   onClick={() => setAnglesStoryboard("none")}
                   style={{ alignSelf: "flex-start", opacity: 0.8 }}
                 >
                   Reset view
                 </button>
               </div>
               <p style={{ margin: 0, fontSize: FONT.base, lineHeight: 1.5, color: "var(--ink)" }}>
                 {definerAnglesDescription}
               </p>
             </aside>
             <div style={{ flex: 1, minWidth: 0 }}>{stage}</div>
           </div>
         );
       }

       if (_isDefiner) {
         return (
           <div
              style={{
                display: "flex",
                gap: 16,
                padding: 12,
                alignItems: "stretch"
              }}
            >
              <DefinitionPanel />
              <div style={{ flex: 1, minWidth: 0 }}>{stage}</div>
            </div>
          );
       }

        return (
          <div
            style={{
              display: "flex",
              gap: 12,
              padding: 12,
              alignItems: "stretch"
            }}
          >
            <div style={{ flex: "0 0 200px", display: "flex", flexDirection: "column", gap: 8 }}>
              <MeasureTable title="Sides" rows={[["AB", fmt(len.AB)],["BC", fmt(len.BC)],["CD", fmt(len.CD)],["DA", fmt(len.DA)]]} />
              <MeasureTable
                title="Angles (°)"
                rows={[["∠A", Math.round(ang.A)],["∠B", Math.round(ang.B)],["∠C", Math.round(ang.C)],["∠D", Math.round(ang.D)],["Sum", Math.round(angleSum)]]}
                tone={approx(angleSum, 360, 1) ? "ok" : "warn"}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>{stage}</div>
            <div style={{ flex: "0 0 200px", display: "flex", flexDirection: "column", gap: 8 }}>
              <MeasureTable title="Diagonals" rows={[["AC", fmt(diagLen.AC)],["BD", fmt(diagLen.BD)],["Equal?", yesNo(approx(diagLen.AC, diagLen.BD, 1))],["⊥ ?", yesNo(diagonalsPerp)]]} />
              <MeasureTable title="Parallels" rows={[["AB ∥ CD", yesNo(parallel.AB_CD)],["BC ∥ DA", yesNo(parallel.BC_DA)],["Any pair", yesNo(parallel.anyPair)],["Both pairs", yesNo(parallel.bothPairs)]]} />
              {_isRect && (
                <MeasureTable
                  title="Rectangle (l×b)"
                  rows={[
                    ["l", fmt(rect.wUnits)],
                    ["b", fmt(rect.hUnits)],
                    ["Area", fmt(rect.wUnits * rect.hUnits)],
                    ["Perimeter", fmt(2 * (rect.wUnits + rect.hUnits))],
                    ["Diagonal", fmt(Math.hypot(rect.wUnits, rect.hUnits))]
                  ]}
                  tone="info"
                />
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* -------------------------- Helpers -------------------------- */

const PIXELS_PER_UNIT = 8;

function Chip({ label, tone = "neutral" }) {
  const bg =
    tone === "ok"   ? "rgba(34,197,94,0.18)" :
    tone === "warn" ? "rgba(245,158,11,0.18)" :
    tone === "info" ? "rgba(106,169,255,0.18)" :
                      "var(--surface-chip)";
  const bd =
    tone === "ok"   ? "rgba(34,197,94,0.45)" :
    tone === "warn" ? "rgba(245,158,11,0.45)" :
    tone === "info" ? "rgba(106,169,255,0.45)" :
                      "var(--border-soft)";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 8px", borderRadius: 999, fontWeight: 700,
      background: bg, border: `1px solid ${bd}`, color: "var(--ink)"
    }}>{label}</span>
  );
}

function MeasureTable({ title, rows = [], tone }) {
  const bd =
    tone === "ok"   ? "rgba(34,197,94,0.55)" :
    tone === "warn" ? "rgba(245,158,11,0.55)" :
    tone === "info" ? "rgba(106,169,255,0.55)" :
                      "var(--border-soft)";
  return (
    <div style={{
      background: "var(--surface-card)",
      border: `1px solid ${bd}`,
      borderRadius: "var(--radius-md)",
      minWidth: 160,
      boxShadow: "var(--shadow-soft)"
    }}>
      <div style={{
        padding: "6px 8px", borderBottom: "1px solid var(--border-soft)",
        fontWeight: 700, fontSize: FONT.sm, color: "var(--ink)"
      }}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i}>
              <td style={{ padding: "6px 8px", fontSize: FONT.sm, color: "var(--ink-muted)" }}>{k}</td>
              <td style={{ padding: "6px 8px", fontSize: FONT.sm, color: "var(--ink)", textAlign: "right" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function extendBeyondVertex(neighbor, vertex, length, invert = false) {
  const direction = invert
    ? { x: neighbor.x - vertex.x, y: neighbor.y - vertex.y }
    : { x: vertex.x - neighbor.x, y: vertex.y - neighbor.y };
  const unit = norm(direction);
  return {
    x: vertex.x + unit.x * length,
    y: vertex.y + unit.y * length
  };
}

function externalAngleLabelPosition(vertex, extPoint, nextPoint, distance = 36) {
  const dirA = norm(vec(vertex, extPoint));
  const dirB = norm(vec(vertex, nextPoint));
  let combined = { x: dirA.x + dirB.x, y: dirA.y + dirB.y };
  const magnitude = Math.hypot(combined.x, combined.y);
  if (magnitude < 1e-3) {
    combined = { x: -dirB.y, y: dirB.x };
  } else {
    combined = { x: combined.x / magnitude, y: combined.y / magnitude };
  }
  return {
    x: vertex.x + combined.x * distance,
    y: vertex.y + combined.y * distance
  };
}

function arcFromDirections(center, dirA, dirB, radius) {
  const p1 = { x: center.x + dirA.x * radius, y: center.y + dirA.y * radius };
  const p2 = { x: center.x + dirB.x * radius, y: center.y + dirB.y * radius };
  const sweep = cross(dirA, dirB) < 0 ? 0 : 1;
  return `M${p1.x},${p1.y} A ${radius},${radius} 0 0,${sweep} ${p2.x},${p2.y}`;
}

function buildDiagonalAngleArcs(center, ordered, radius = 30) {
  if (!center || !ordered?.length) return [];
  const dirs = ordered.slice(0, 4).map((point) => norm(vec(center, point)));
  const combos = [
    [dirs[0], dirs[1]],
    [dirs[1], dirs[2]],
    [dirs[2], dirs[3]],
    [dirs[3], dirs[0]]
  ];
  return combos.map(([a, b], index) => arcFromDirections(center, a, b, radius - (index % 2) * 5));
}

function DefinitionPanel() {
  return (
    <div
      style={{
        flex: "0 0 260px",
        minHeight: 0,
        padding: "16px",
        background: "var(--surface-subtle)",
        border: "1px solid var(--border-soft)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-soft)",
        display: "flex",
        flexDirection: "column",
        gap: 12
      }}
    >
      <h3 style={{ margin: 0, fontSize: FONT.lg, fontWeight: 800, color: "var(--ink)" }}>Quadrilateral terms</h3>
      <ul style={{ margin: 0, paddingLeft: 18, color: "var(--ink)", fontSize: FONT.base, lineHeight: 1.45 }}>
        <li><strong>Vertices</strong>: The four corner points (A,B,C,D).</li>
        <li><strong>Sides</strong>: The four line segments that form the boundary (AB,BC,CD,DA).</li>
        <li><strong>Interior angles</strong>: Angles formed at each vertex inside the shape (∠A,∠B,∠C,∠D).</li>
        <li><strong>Diagonals</strong>: Line segments joining opposite (non-adjacent) vertices (AC,BD).</li>
      </ul>
    </div>
  );
}

function centroid(points) {
  if (!points || !points.length) return { x: 0, y: 0 };
  const total = points.reduce((acc, point) => {
    acc.x += point.x;
    acc.y += point.y;
    return acc;
  }, { x: 0, y: 0 });
  return { x: total.x / points.length, y: total.y / points.length };
}

function snapPointToAngle(point, centre, snapDeg) {
  if (!snapDeg) return point;
  const dx = point.x - centre.x;
  const dy = point.y - centre.y;
  const radius = Math.hypot(dx, dy) || 1;
  let angle = Math.atan2(dy, dx);
  const snapRad = (snapDeg * Math.PI) / 180;
  angle = Math.round(angle / snapRad) * snapRad;
  return {
    x: centre.x + Math.cos(angle) * radius,
    y: centre.y + Math.sin(angle) * radius
  };
}

function buildPerimeterMeta(points) {
  if (!points || points.length < 2) return { segments: [], length: 0 };
  let length = 0;
  const segments = points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    const segLength = Math.hypot(next.x - point.x, next.y - point.y);
    const start = length;
    length += segLength;
    return { index, start, end: length, length: segLength };
  });
  return { segments, length: length || 1 };
}

function pointAlongPerimeter(points, meta, progress) {
  if (!meta || !meta.segments.length) return null;
  const distance = (progress % 1) * meta.length;
  const segment = meta.segments.find((seg) => distance <= seg.end) || meta.segments[meta.segments.length - 1];
  const local = segment.length ? (distance - segment.start) / segment.length : 0;
  const startPoint = points[segment.index];
  const endPoint = points[(segment.index + 1) % points.length];
  const x = startPoint.x + (endPoint.x - startPoint.x) * local;
  const y = startPoint.y + (endPoint.y - startPoint.y) * local;
  const heading = Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x);
  return { point: { x, y }, heading, edgeIndex: segment.index, progress: local };
}

function makeTrapeziumPreset(variant, W, H) {
  const cx = W / 2;
  const cy = H / 2;
  const baseHeight = 150;

  if (variant === "isosceles") {
    const top = 200;
    const bottom = 320;
    const h = baseHeight;
    return [
      { x: cx - top / 2, y: cy - h / 2 },
      { x: cx + top / 2, y: cy - h / 2 },
      { x: cx + bottom / 2, y: cy + h / 2 },
      { x: cx - bottom / 2, y: cy + h / 2 }
    ];
  }

  if (variant === "right") {
    const top = 210;
    const bottom = 320;
    const h = baseHeight;
    const left = cx - bottom / 2;
    return [
      { x: left, y: cy - h / 2 },
      { x: left + top, y: cy - h / 2 },
      { x: left + bottom, y: cy + h / 2 },
      { x: left, y: cy + h / 2 }
    ];
  }

  // default scalene preset
  const top = 190;
  const bottom = 320;
  const h = baseHeight;
  const leftShift = 70;
  const rightShift = 40;
  return [
    { x: cx - top / 2 - leftShift, y: cy - h / 2 },
    { x: cx + top / 2 + rightShift, y: cy - h / 2 },
    { x: cx + bottom / 2, y: cy + h / 2 },
    { x: cx - bottom / 2 - 80, y: cy + h / 2 }
  ];
}

function projectPointOntoLine(point, start, end) {
  const abx = end.x - start.x;
  const aby = end.y - start.y;
  const lengthSq = abx * abx + aby * aby || 1;
  const t = ((point.x - start.x) * abx + (point.y - start.y) * aby) / lengthSq;
  return {
    x: start.x + abx * t,
    y: start.y + aby * t,
    t
  };
}

function distancePointToLine(point, start, end) {
  const A = end.y - start.y;
  const B = start.x - end.x;
  const C = end.x * start.y - end.y * start.x;
  return Math.abs(A * point.x + B * point.y + C) / Math.hypot(A, B);
}

function computeTrapeziumAreaDetails(points) {
  if (!points || points.length < 4) return null;
  const [A, B, C, D] = points;
  const AB = vec(A, B);
  const BC = vec(B, C);
  const CD = vec(C, D);
  const DA = vec(D, A);

  let shorter;
  let longer;

  if (isParallel(AB, CD)) {
    const lenAB = vlen(AB);
    const lenCD = vlen(CD);
    if (lenAB <= lenCD) {
      shorter = { points: [A, B], length: lenAB };
      longer = { points: [C, D], length: lenCD };
    } else {
      shorter = { points: [C, D], length: lenCD };
      longer = { points: [A, B], length: lenAB };
    }
  } else if (isParallel(BC, DA)) {
    const lenBC = vlen(BC);
    const lenDA = vlen(DA);
    if (lenBC <= lenDA) {
      shorter = { points: [B, C], length: lenBC };
      longer = { points: [D, A], length: lenDA };
    } else {
      shorter = { points: [D, A], length: lenDA };
      longer = { points: [B, C], length: lenBC };
    }
  } else {
    return null;
  }

  const [s1, s2] = shorter.points;
  const [longStart, longEnd] = longer.points;

  const height = distancePointToLine(s1, longStart, longEnd);

  const proj1 = projectPointOntoLine(s1, longStart, longEnd);
  const proj2 = projectPointOntoLine(s2, longStart, longEnd);
  let startT = Math.min(proj1.t, proj2.t);
  let endT = Math.max(proj1.t, proj2.t);

  const clampedStart = clamp(startT, 0, 1);
  const clampedEnd = clamp(endT, 0, 1);

  const longerLen = longer.length;
  const rectangleWidth = Math.max(clampedEnd - clampedStart, 0) * longerLen;
  const a1 = Math.max(clampedStart, 0) * longerLen;
  const a2 = Math.max(1 - clampedEnd, 0) * longerLen;
  const shorterLen = shorter.length;

  const triangleLeft = 0.5 * a1 * height;
  const triangleRight = 0.5 * a2 * height;
  const rectangleArea = rectangleWidth * height;
  const total = 0.5 * (longerLen + shorterLen) * height;

  return {
    longerLength: longerLen,
    shorterLength: shorterLen,
    height,
    rectangleWidth,
    a1,
    a2,
    triangleLeft,
    triangleRight,
    rectangleArea,
    total
  };
}

function computeParallelogramAreaDetails(points) {
  if (!points || points.length < 4) return null;
  const [A, B, C] = points;
  const base = vlen(vec(A, B));
  if (base <= 0.0001) return null;
  const height = distancePointToLine(C, A, B);
  const triangle = 0.5 * base * height;
  const total = base * height;
  return { base, height, triangle, total };
}

function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
function approx(a, b, eps = 0.02 * Math.max(1, Math.abs(b))) { return Math.abs(a - b) <= eps; }
function vec(p, q) { return { x: q.x - p.x, y: q.y - p.y }; }
function vlen(v) { return Math.hypot(v.x, v.y); }
function dot(a, b) { return a.x * b.x + a.y * b.y; }
function cross(a, b) { return a.x * b.y - a.y * b.x; }
function norm(v) { const L = vlen(v) || 1; return { x: v.x / L, y: v.y / L }; }
function yesNo(x) { return x ? "Yes" : "No"; }
function fmt(n) { return (Math.round(n * 10) / 10).toFixed(1); }
function roundTo(n, step = 1) { return Math.round(n / step) * step; }

function angleAt(prev, cur, next) {
  const v1 = vec(cur, prev);
  const v2 = vec(cur, next);
  const L = (vlen(v1) * vlen(v2)) || 1;
  const cos = clamp(dot(v1, v2) / L, -1, 1);
  return (Math.acos(cos) * 180) / Math.PI;
}

function isParallel(a, b) { return Math.abs(cross(norm(a), norm(b))) < 0.03; }
function isPerpendicular(a, b) { return Math.abs(dot(norm(a), norm(b))) < 0.03; }
function labelFor(i) { return ["A","B","C","D"][i] || ""; }
function midpoint(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }

function offsetPoint(base, direction, distance) {
  const L = Math.hypot(direction.x, direction.y) || 1;
  return {
    x: base.x + (direction.x / L) * distance,
    y: base.y + (direction.y / L) * distance
  };
}

function computeVertexLabel(vertex, center) {
  const dir = { x: vertex.x - center.x, y: vertex.y - center.y };
  const position = offsetPoint(vertex, dir, 18);
  let anchor = "middle";
  if (Math.abs(dir.x) > 6) {
    anchor = dir.x < 0 ? "end" : "start";
  }
  return { x: position.x, y: position.y, anchor };
}

function computeAngleLabel(vertex, center) {
  const inward = { x: center.x - vertex.x, y: center.y - vertex.y };
  const position = offsetPoint(vertex, inward, 22);
  return { x: position.x, y: position.y };
}

function canonicalRotation(angleDeg) {
  let a = angleDeg;
  while (a > 180) a -= 360;
  while (a <= -180) a += 360;
  if (a > 90) a -= 180;
  if (a < -90) a += 180;
  return a;
}

function computeSideLabel(p, q, center) {
  const mid = midpoint(p, q);
  const inward = { x: center.x - mid.x, y: center.y - mid.y };
  const pos = offsetPoint(mid, inward, 16);
  const angleDeg = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;
  return { x: pos.x, y: pos.y, rotation: canonicalRotation(angleDeg) };
}

function computeDiagonalLabel(p, q) {
  const mid = midpoint(p, q);
  const dir = vec(p, q);
  const pos = offsetPoint(mid, { x: -dir.x, y: -dir.y }, 28);
  const angleDeg = (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
  return { x: pos.x, y: pos.y, rotation: canonicalRotation(angleDeg) };
}

function enforceOrder(points) {
  const area2 =
    points[0].x * points[1].y - points[1].x * points[0].y +
    points[1].x * points[2].y - points[2].x * points[1].y +
    points[2].x * points[3].y - points[3].x * points[2].y +
    points[3].x * points[0].y - points[0].x * points[3].y;
  return area2 > 0 ? [points[0], points[3], points[2], points[1]] : points;
}

function arcPath(center, from, to, r = 18) {
  const v1 = norm(vec(center, from));
  const v2 = norm(vec(center, to));
  const p1 = { x: center.x + v1.x * r, y: center.y + v1.y * r };
  const p2 = { x: center.x + v2.x * r, y: center.y + v2.y * r };
  const sweep = cross(v1, v2) < 0 ? 0 : 1;
  return `M${p1.x},${p1.y} A ${r},${r} 0 0,${sweep} ${p2.x},${p2.y}`;
}

function lineIntersect(p1, p2, p3, p4) {
  const x1=p1.x,y1=p1.y,x2=p2.x,y2=p2.y,x3=p3.x,y3=p3.y,x4=p4.x,y4=p4.y;
  const den = (x1-x2)*(y3-y4) - (y1-y2)*(x3-x4);
  if (Math.abs(den) < 1e-6) return null;
  const xi = ((x1*y2 - y1*x2)*(x3-x4) - (x1-x2)*(x3*y4 - y3*x4)) / den;
  const yi = ((x1*y2 - y1*x2)*(y3-y4) - (y1-y2)*(x3*y4 - y3*x4)) / den;
  return { x: xi, y: yi };
}

function polygonArea(pts) {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    s += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return 0.5 * s;
}

function makeInitial(kind, W, H) {
  const cx = W / 2, cy = H / 2;
  const pad = 70;
  if (kind === "general" || kind === "random") {
    // fixed non-special convex quad
    return [
      { x: cx - 160, y: cy - 90 },
      { x: cx + 120, y: cy - 50 },
      { x: cx + 80,  y: cy + 110 },
      { x: cx - 140, y: cy + 60 },
    ];
  }
  if (kind === "balanced") {
    const topHalf = 120;
    const bottomHalf = 170;
    const vertical = 80;
    return [
      { x: cx - topHalf, y: cy - vertical },   // A
      { x: cx - bottomHalf, y: cy + vertical },// B
      { x: cx + bottomHalf, y: cy + vertical },// C
      { x: cx + topHalf, y: cy - vertical },    // D
    ];
  }
  if (kind === "rectangle") {
    const w = 220, h = 140;
    return [
      { x: cx - w/2, y: cy - h/2 },
      { x: cx + w/2, y: cy - h/2 },
      { x: cx + w/2, y: cy + h/2 },
      { x: cx - w/2, y: cy + h/2 },
    ];
  }
  if (kind === "square") {
    const s = 180;
    return [
      { x: cx - s/2, y: cy - s/2 },
      { x: cx + s/2, y: cy - s/2 },
      { x: cx + s/2, y: cy + s/2 },
      { x: cx - s/2, y: cy + s/2 },
    ];
  }
  if (kind === "parallelogram") {
    const w = 240, h = 140, skew = 70;
    return [
      { x: cx - w/2, y: cy - h/2 },
      { x: cx + w/2, y: cy - h/2 },
      { x: cx + w/2 - skew, y: cy + h/2 },
      { x: cx - w/2 - skew, y: cy + h/2 },
    ];
  }
  if (kind === "rhombus") {
    const rx = 120, ry = 80;
    return [
      { x: cx - rx, y: cy },
      { x: cx, y: cy - ry },
      { x: cx + rx, y: cy },
      { x: cx, y: cy + ry },
    ];
  }
  if (kind === "kite") {
    return [
      { x: cx - 120, y: cy },
      { x: cx, y: cy - 80 },
      { x: cx + 90, y: cy },
      { x: cx, y: cy + 120 },
    ];
  }
  if (kind === "trapezium") {
    return makeTrapeziumPreset("scalene", W, H);
  }
  // fallback random convex-ish
  return [
    { x: rand(pad, W - pad), y: rand(pad, H - pad) },
    { x: rand(pad, W - pad), y: rand(pad, H - pad) },
    { x: rand(pad, W - pad), y: rand(pad, H - pad) },
    { x: rand(pad, W - pad), y: rand(pad, H - pad) },
  ];
}
function rand(a, b) { return a + Math.random() * (b - a); }

// Rectangle helpers
function makeRect(l, b, W, H, min, max) {
  const lClamped = clamp(l, min, max);
  const bClamped = clamp(b, min, max);
  const cx = W / 2, cy = H / 2;
  return {
    cx, cy,
    wUnits: lClamped,
    hUnits: bClamped,
    get w() { return this.wUnits * PIXELS_PER_UNIT; },
    get h() { return this.hUnits * PIXELS_PER_UNIT; },
  };
}
function rectToPts(r) {
  return [
    { x: r.cx - r.w/2, y: r.cy - r.h/2 }, // A
    { x: r.cx + r.w/2, y: r.cy - r.h/2 }, // B
    { x: r.cx + r.w/2, y: r.cy + r.h/2 }, // C
    { x: r.cx - r.w/2, y: r.cy + r.h/2 }, // D
  ];
}
